import {
  FileText, Plane, Bus, Hotel, Shield, CheckCircle, KeyRound, BookUser, Ship,
  Receipt, FileMinus, FilePlus, Loader2, Download,
  RefreshCw, AlertTriangle, Clock, Send, Eye, CalendarClock, BellRing,
} from "lucide-react";
import { formatDMY } from "../../../../utils/dateFormat";
import base from "../BookingDetail.module.css";
import s from "./documents.module.css";

/* ── Maps ── */
export const SUBTYPE_ICONS = {
  flight_voucher: Plane,
  transfer_voucher: Bus,
  car_rental_voucher: KeyRound,
  visa_voucher: BookUser,
  cruise_voucher: Ship,
  hotel_voucher: Hotel,
  sales_invoice: Receipt,
  credit_invoice: FileMinus,
  debit_notes: FilePlus,
  booking_confirmation: CheckCircle,
  insurance_confirmation: Shield,
  ssr_confirmation: FileText,
  payment_request: CalendarClock,
  payment_reminder: BellRing,
};

export const SUBTYPE_LABELS = {
  flight_voucher: "Flight Voucher",
  transfer_voucher: "Transfer Voucher",
  car_rental_voucher: "Car Rental Voucher",
  visa_voucher: "Visa Confirmation",
  cruise_voucher: "Cruise Voucher",
  hotel_voucher: "Hotel Voucher",
  sales_invoice: "Sales Invoice",
  credit_invoice: "Credit Note",
  debit_notes: "Debit Note",
  booking_confirmation: "Booking Confirmation",
  insurance_confirmation: "Insurance Confirmation",
  ssr_confirmation: "SSR Confirmation",
  payment_request: "Payment Request",
  payment_reminder: "Payment Reminder",
};

export const STATUS_BADGES = {
  "0": { label: "Issued", cls: "statusIssued" },
  "1": { label: "Sent", cls: "statusSent" },
  "2": { label: "Draft", cls: "statusDraft" },
};

/* ── Formatters ──
   Currency follows the EU display convention used across the booking screen:
   "€" symbol, dot thousands, comma decimals, exactly 2 decimals (e.g. € 1.500,00). */
const _money = new Intl.NumberFormat("nl-BE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const formatMoney = (n, currency = "EUR") => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "—";
  const sym = currency === "EUR" ? "€" : `${currency} `;
  return `${sym} ${_money.format(Number(n))}`;
};

export const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};

/* Compact relative time ("just now", "3 minutes ago", "2 days ago"); falls back
   to an absolute date past a month. Returns null for missing/invalid input so
   callers can omit the hint entirely. Future timestamps clamp to "just now". */
export const formatRelative = (dateStr) => {
  if (!dateStr) return null;
  const t = new Date(dateStr).getTime();
  if (Number.isNaN(t)) return null;
  const diff = Math.max(0, Date.now() - t);
  const MIN = 60000, HR = 3600000, DAY = 86400000;
  if (diff < MIN) return "just now";
  if (diff < HR) { const m = Math.floor(diff / MIN); return `${m} minute${m !== 1 ? "s" : ""} ago`; }
  if (diff < DAY) { const h = Math.floor(diff / HR); return `${h} hour${h !== 1 ? "s" : ""} ago`; }
  if (diff < 30 * DAY) { const d = Math.floor(diff / DAY); return `${d} day${d !== 1 ? "s" : ""} ago`; }
  return formatDate(dateStr);
};

/* ── Document row ──
   variant: "voucher" | "invoice" | "confirmation". Invoices additionally render
   a right-aligned amount block and VAT / linked-invoice chips. */
export const DocRow = ({ doc, index = 0, isNew = false, onDownload, onView, downloading, onSend, regenerating = false }) => {
  const Icon = SUBTYPE_ICONS[doc.documentSubtype] || FileText;
  const badge = STATUS_BADGES[doc.status] || STATUS_BADGES["2"];
  const isInvoice = doc.documentType === "INVOICE";
  const isCredit = doc.documentSubtype === "credit_invoice";

  // Block download while the latest PDF is being (re)built. A fresh generation or
  // an in-progress regeneration nulls pdfUrl until the background render finishes;
  // `regenerating` additionally covers the brief window while the request itself is
  // in flight. This stops the user grabbing a stale or half-written copy — it
  // re-enables automatically once the poller picks up the new pdfUrl.
  const preparing = regenerating || (!doc.pdfUrl && !doc.pdfStuck);

  return (
    <div
      className={s.row}
      style={{ animation: isNew ? `docSlideIn 0.4s ease-out ${index * 0.1}s both` : "none" }}
    >
      <div className={s.rowLeft}>
        <div className={`${s.rowIcon} ${isInvoice ? s.rowIconInvoice : ""}`}>
          <Icon size={16} />
        </div>
        <div style={{ minWidth: 0 }}>
          <div className={s.rowName}>
            {SUBTYPE_LABELS[doc.documentSubtype] || doc.subtypeLabel}
          </div>
          <div className={s.rowMeta}>
            <span>{doc.documentNumber}</span>
            <span className={s.rowMetaDot}>·</span>
            <span>{formatDate(doc.issueDate)}</span>
            <span className={`${s.chip} ${s[badge.cls]}`}>{badge.label}</span>
            {isInvoice && doc.vatType && (
              <span className={`${s.chip} ${s.vatChip}`}>{doc.vatType}</span>
            )}
            {isInvoice && doc.linkedInvoiceNumber && (
              <span className={`${s.chip} ${s.chipNeutral}`}>↗ {doc.linkedInvoiceNumber}</span>
            )}
          </div>
        </div>
      </div>

      <div className={s.rowRight}>
        {isInvoice && doc.amount != null && (
          <div className={s.amountBlock}>
            <div className={`${s.amountValue} ${isCredit ? s.amountValueCredit : ""}`}>
              {isCredit ? "− " : ""}{formatMoney(Math.abs(doc.amount), doc.currency)}
            </div>
            <div className={s.amountCaption}>
              {doc.dueDate
                ? `${doc.documentSubtype === "payment_reminder" ? "Was due" : "Due"} ${formatDMY(doc.dueDate)}`
                : doc.vatType ? "VAT incl." : "Total"}
            </div>
          </div>
        )}
        {onView && (
          <button
            className={base.btnSecondary}
            onClick={() => onView(doc)}
            disabled={preparing}
            title="View the document. One that is not released for customer use is marked as an internal review copy."
          >
            <Eye size={12} />
            View
          </button>
        )}
        {onSend && (
          <button
            className={base.btnSecondary}
            onClick={() => onSend(doc)}
            disabled={preparing}
            title={preparing
              ? "The PDF is still being prepared — sending will enable when it's ready"
              : "Review and send this document by email or WhatsApp"}
          >
            <Send size={12} />
            Send
          </button>
        )}
        <button
          className={base.btnSecondary}
          onClick={() => onDownload(doc)}
          disabled={preparing || downloading}
          title={preparing
            ? "The latest PDF is being prepared — download will enable when it's ready"
            : "Download the customer-ready PDF (only when the document is released)"}
        >
          {(preparing || downloading)
            ? <Loader2 size={12} style={{ animation: "docSpin 1s linear infinite" }} />
            : <Download size={12} />}
          {preparing ? "Preparing…" : "Download"}
        </button>
      </div>
    </div>
  );
};

/* ── Generating overlay ── */
export const GeneratingOverlay = ({ title, subtitle, accent = "#2563eb" }) => (
  <div className={s.generating} style={{ "--accent": accent }}>
    <div className={s.genRing}>
      <div className={s.genRingTrack} />
      <div className={s.genRingIcon}><FileText size={22} /></div>
    </div>
    <div>
      <div className={s.genTitle}>{title}</div>
      {subtitle && <div className={s.genSubtitle}>{subtitle}</div>}
    </div>
    <div className={s.genDots}>
      {[0, 1, 2].map((i) => (
        <div key={i} className={s.genDot} style={{ animationDelay: `${i * 0.16}s` }} />
      ))}
    </div>
  </div>
);

/* ── Skeleton list (loading) ── */
export const SkeletonList = ({ count = 3 }) => (
  <div className={s.list}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className={s.skelRow}>
        <div className={s.skelLeft}>
          <div className={`${s.skelBlock} ${s.shimmer}`} style={{ width: 36, height: 36 }} />
          <div className={s.skelText}>
            <div className={`${s.skelBlock} ${s.shimmer}`} style={{ width: "38%", height: 11 }} />
            <div className={`${s.skelBlock} ${s.shimmer}`} style={{ width: "62%", height: 9 }} />
          </div>
        </div>
        <div className={`${s.skelBlock} ${s.shimmer}`} style={{ width: 92, height: 30 }} />
      </div>
    ))}
  </div>
);

/* ── Empty state ── */
export const EmptyState = ({ Icon = FileText, title, hint, action }) => (
  <div className={s.empty}>
    <div className={s.emptyIcon}><Icon size={26} /></div>
    <div className={s.emptyTitle}>{title}</div>
    {hint && <div className={s.emptyHint}>{hint}</div>}
    {action && <div className={s.emptyAction}>{action}</div>}
  </div>
);

/* ── Background-saving hint banner ── */
export const SavingBanner = () => (
  <div className={s.savingBanner}>
    <Loader2 size={14} style={{ animation: "docSpin 1s linear infinite" }} />
    <span>Preparing the latest PDF(s) in the background — downloads enable automatically once each is ready.</span>
  </div>
);

/* ── Generate / Regenerate action button ──
   A single primary button whose intent flips on whether documents already exist:
   "Generate <noun>" when empty, "Regenerate <noun>" (with a refresh glyph) when
   present. While running it shows the matching gerund + a spinner, and stays
   disabled so a double-click can never fire two overlapping generations. */
export const GenerateButton = ({ count = 0, generating = false, onClick, noun = "Documents", disabled = false }) => {
  const isRegen = count > 0;
  const Icon = generating ? Loader2 : isRegen ? RefreshCw : FilePlus;
  const label = generating
    ? isRegen ? "Regenerating…" : "Generating…"
    : isRegen ? `Regenerate ${noun}` : `Generate ${noun}`;
  return (
    <button
      className={base.btnPrimary}
      onClick={onClick}
      disabled={generating || disabled}
      title={generating
        ? (isRegen ? "Regenerating…" : "Generating…")
        : disabled
          ? `Please wait — the previous ${noun.toLowerCase()}’ PDFs are still being prepared`
          : isRegen
            ? `Rebuild these ${noun.toLowerCase()} from the latest booking details`
            : `Generate ${noun.toLowerCase()} from this booking`}
    >
      <Icon size={13} style={generating ? { animation: "docSpin 1s linear infinite" } : undefined} />
      {label}
    </button>
  );
};

/* ── Freshness / staleness note ──
   When documents exist this shows when they were last generated. If the booking
   was edited AFTER that point, it switches to an amber "regenerate to refresh"
   banner with an inline action, so out-of-date documents are never silent. */
export const FreshnessNote = ({ lastGeneratedAt, isStale = false, onRegenerate, regenerating = false, pending = false }) => {
  const rel = formatRelative(lastGeneratedAt);
  const busy = regenerating || pending;

  if (isStale) {
    return (
      <div className={s.staleBanner} role="status">
        <AlertTriangle size={15} />
        <span>
          This booking has changed{rel ? ` since these documents were generated (${rel})` : ""}.
          Regenerate to refresh them with the latest details.
        </span>
        {onRegenerate && (
          <button
            type="button"
            className={s.staleAction}
            onClick={onRegenerate}
            disabled={busy}
            title={pending && !regenerating
              ? "Please wait — PDFs are still being prepared"
              : "Regenerate now with the latest booking details"}
          >
            {busy
              ? <Loader2 size={12} style={{ animation: "docSpin 1s linear infinite" }} />
              : <RefreshCw size={12} />}
            Regenerate now
          </button>
        )}
      </div>
    );
  }

  if (!rel) return null;

  return (
    <div className={s.freshNote}>
      <Clock size={13} />
      <span>Last generated {rel}</span>
    </div>
  );
};
